import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, CheckCircle, AlertTriangle, Upload, X, FileImage, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACLvHUxODkLRRLjP';

// File constraints (must match backend)
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB per file
const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10 MB total
const MAX_FILES = 3;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.pdf'];

const SupportPage = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const fileInputRef = useRef(null);

  // Set email from user context
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  // Load Turnstile
  useEffect(() => {
    let mounted = true;
    
    const initTurnstile = () => {
      if (mounted && window.turnstile && turnstileRef.current && !turnstileWidgetId.current) {
        try {
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (token) => {
              if (mounted) setTurnstileToken(token);
            },
            'expired-callback': () => {
              if (mounted) setTurnstileToken('');
            },
            'error-callback': () => {
              console.log('Turnstile widget error');
              if (mounted) setTurnstileError(true);
            },
            theme: 'light',
            language: 'ru'
          });
        } catch (e) {
          console.log('Turnstile render error:', e);
        }
      }
    };
    
    if (window.turnstile) {
      setTimeout(initTurnstile, 500);
    } else {
      const existingScript = document.querySelector('script[src*="turnstile"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onerror = () => {
          console.log('Turnstile script failed to load');
        };
        script.onload = () => setTimeout(initTurnstile, 500);
        document.head.appendChild(script);
      } else {
        const checkTurnstile = setInterval(() => {
          if (window.turnstile) {
            clearInterval(checkTurnstile);
            initTurnstile();
          }
        }, 200);
        setTimeout(() => clearInterval(checkTurnstile), 10000);
      }
    }
    
    return () => {
      mounted = false;
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (e) {
          // ignore
        }
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  const resetTurnstile = () => {
    if (turnstileWidgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (e) {
        // ignore
      }
    }
    setTurnstileToken('');
  };

  // Reinitialize Turnstile widget (for when returning to form after success)
  const reinitializeTurnstile = () => {
    // Remove existing widget if present
    if (turnstileWidgetId.current && window.turnstile) {
      try {
        window.turnstile.remove(turnstileWidgetId.current);
      } catch (e) {
        // ignore
      }
      turnstileWidgetId.current = null;
    }
    
    // Render new widget
    if (window.turnstile && turnstileRef.current) {
      try {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileError(true),
          theme: 'light',
          language: 'ru'
        });
      } catch (e) {
        console.log('Turnstile reinit error:', e);
      }
    }
    setTurnstileToken('');
    setTurnstileError(false);
  };

  // Handle returning to form after success
  const handleSendAnother = () => {
    setSuccess(false);
    // Use setTimeout to ensure DOM is updated before reinitializing
    setTimeout(() => {
      reinitializeTurnstile();
    }, 100);
  };

  // File handling functions
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' Б';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' КБ';
    return (bytes / (1024 * 1024)).toFixed(1) + ' МБ';
  };

  const getTotalSize = useCallback(() => {
    return files.reduce((sum, f) => sum + f.size, 0);
  }, [files]);

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      const ext = file.name.toLowerCase().split('.').pop();
      if (!ALLOWED_EXTENSIONS.some(e => e.slice(1) === ext)) {
        return `Недопустимый тип файла: ${file.name}. Разрешены: jpg, png, webp, pdf`;
      }
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return `Файл ${file.name} превышает 5 МБ`;
    }
    
    return null;
  };

  const handleFileSelect = (selectedFiles) => {
    const newFiles = Array.from(selectedFiles);
    const errors = [];
    const validFiles = [];
    
    if (files.length + newFiles.length > MAX_FILES) {
      setError(`Максимум ${MAX_FILES} файла. У вас уже ${files.length}.`);
      return;
    }
    
    let newTotalSize = getTotalSize();
    
    for (const file of newFiles) {
      if (files.some(f => f.name === file.name && f.size === file.size)) {
        errors.push(`Файл ${file.name} уже добавлен`);
        continue;
      }
      
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
        continue;
      }
      
      if (newTotalSize + file.size > MAX_TOTAL_SIZE) {
        errors.push(`Общий размер файлов превысит 10 МБ`);
        break;
      }
      
      newTotalSize += file.size;
      validFiles.push(file);
    }
    
    if (errors.length > 0) {
      setError(errors[0]);
    } else {
      setError('');
    }
    
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setError('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles);
    }
  };

  const getFileIcon = (file) => {
    if (file.type === 'application/pdf') {
      return <FileText className="h-4 w-4 text-red-500" />;
    }
    return <FileImage className="h-4 w-4 text-blue-500" />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email.trim()) {
      setError('Введите email');
      return;
    }
    
    if (!message.trim() || message.trim().length < 10) {
      setError('Сообщение должно содержать минимум 10 символов');
      return;
    }
    
    if (!turnstileToken) {
      setError('Пожалуйста, пройдите проверку CAPTCHA');
      return;
    }
    
    setLoading(true);
    
    try {
      let response;
      
      if (files.length > 0) {
        const formData = new FormData();
        formData.append('email', email.trim());
        formData.append('message', message.trim());
        formData.append('turnstileToken', turnstileToken);
        
        files.forEach(file => {
          formData.append('files', file);
        });
        
        response = await axios.post(`${API_URL}/api/support/with-attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${API_URL}/api/support`, {
          email: email.trim(),
          message: message.trim(),
          turnstileToken: turnstileToken
        });
      }
      
      // Axios automatically parses JSON
      const data = response.data;
      
      if (data.success === false) {
        throw new Error(data.detail || 'Ошибка отправки');
      }
      
      setSuccess(true);
      setMessage('');
      setFiles([]);
      
    } catch (err) {
      // Axios error handling
      const errorMessage = err.response?.data?.detail || err.message || 'Произошла ошибка. Попробуйте позже.';
      setError(errorMessage);
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Сообщение отправлено</h2>
              <p className="text-gray-600">
                Мы свяжемся с вами при необходимости.
              </p>
              <Button 
                onClick={handleSendAnother} 
                variant="outline" 
                className="w-full"
              >
                Отправить ещё сообщение
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Обратная связь</CardTitle>
          <CardDescription>
            Опишите вашу проблему или задайте вопрос
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Ваш Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                data-testid="support-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">Сообщение</Label>
              <Textarea
                id="message"
                placeholder="Опишите вашу проблему или вопрос (минимум 10 символов)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                required
                minLength={10}
                maxLength={5000}
                disabled={loading}
                data-testid="support-message-input"
              />
              <p className="text-xs text-gray-500 text-right">
                {message.length}/5000
              </p>
            </div>
            
            {/* File Upload Section */}
            <div className="space-y-2">
              <Label>Вложения (необязательно)</Label>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
                data-testid="file-input"
              />
              
              <div
                onClick={() => !loading && files.length < MAX_FILES && fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                  relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
                  transition-colors duration-200
                  ${isDragging 
                    ? 'border-blue-500 bg-blue-50' 
                    : files.length >= MAX_FILES
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                  }
                  ${loading ? 'opacity-50 pointer-events-none' : ''}
                `}
                data-testid="file-dropzone"
              >
                <Upload className={`mx-auto h-8 w-8 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className="mt-2 text-sm text-gray-600">
                  {files.length >= MAX_FILES 
                    ? 'Достигнут лимит файлов'
                    : isDragging 
                      ? 'Отпустите файл' 
                      : 'Перетащите файлы сюда или нажмите для выбора'
                  }
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  JPG, PNG, WebP, PDF • до 5 МБ на файл • максимум 3 файла
                </p>
              </div>
              
              {files.length > 0 && (
                <div className="space-y-2 mt-3" data-testid="attached-files-list">
                  {files.map((file, index) => (
                    <div 
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {getFileIcon(file)}
                        <span className="text-sm text-gray-700 truncate">{file.name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        disabled={loading}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        data-testid={`remove-file-${index}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 text-right">
                    Общий размер: {formatFileSize(getTotalSize())} / 10 МБ
                  </p>
                </div>
              )}
            </div>
            
            {/* Turnstile CAPTCHA */}
            <div className="flex justify-center flex-col items-center gap-2">
              <div ref={turnstileRef} data-testid="turnstile-widget"></div>
              {turnstileError && (
                <div className="flex items-center gap-2 text-sm text-amber-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span>CAPTCHA недоступна. Попробуйте обновить страницу.</span>
                </div>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !turnstileToken}
              data-testid="support-submit-btn"
            >
              {loading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Отправка...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Отправить сообщение
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
      
      <p className="text-center text-sm text-gray-500 mt-6">
        Мы ответим на ваш email при необходимости
      </p>
    </div>
  );
};

export default SupportPage;
